const destinations = [
  {
    name: "United Kingdom",
    context: "London, Edinburgh, and Manchester draw Orlando's British expat community, Disney resort exchange employees, and business travelers to BAFTA and entertainment industry meetings",
    urgency: "Entertainment industry meetings and film festival dates are fixed — passport issues discovered 2 weeks out need emergency processing",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Mexico & Riviera Maya",
    context: "Cancun, Playa del Carmen, and Cozumel are top all-inclusive picks for Central Florida families. Spring break bookings surge in January; summer deals book in April",
    urgency: "Spring break demand peaks in Jan–Feb — standard processing cannot meet March departure dates",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Bahamas & Caribbean",
    context: "Port Canaveral cruise departures to Nassau, Cozumel, Grand Cayman, and private cruise islands. Disney Cruise Line is the dominant carrier from this port for Orlando families",
    urgency: "Disney cruise bookings often include children with expired 5-year passports not checked until boarding documents arrive",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Germany & Europe",
    context: "Frankfurt, Berlin, and London draw convention attendees from the Orange County Convention Center; Paris and Barcelona attract tourism industry executives and UCF study abroad students",
    urgency: "Trade show and conference dates are non-negotiable — overseas exhibitors need passports before hotel is even booked",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Canada",
    context: "Toronto and Montreal attract business travelers and families visiting relatives; Canadian theme park industry connections bring Orlando executives north regularly",
    urgency: "Business trips to Canada often confirm 10–14 days before departure with minimal advance notice",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Japan & Asia",
    context: "Tokyo Disneyland and Universal Studios Japan attract Orlando theme park professionals on exchange programs and study tours; aerospace contracts draw engineers to Japan and South Korea",
    urgency: "Company-mandated travel for exchange programs often has HR-driven timelines that cannot flex around passport office wait times",
    icon: "ri-map-pin-2-line",
  },
];

const stats = [
  { stat: "71%", label: "of Orlando expedited requests involve families, cruise departures, or study-abroad program deadlines" },
  { stat: "2–3 Weeks", label: "typical notice Orlando travelers have between booking and international departure from MCO" },
  { stat: "Oct & Mar", label: "peak study-abroad and spring break booking seasons that drive passport urgency spikes" },
  { stat: "48 Hours", label: "shortest cruise emergency we\'ve accommodated for a Port Canaveral sailing family" },
];

export default function OrlandoDestinationsSection() {
  return (
    <section className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Where Orlando Travelers Go — And Why Passports Get Urgent
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            From theme park family cruises out of Port Canaveral to UCF study-abroad semesters and trade show follow-up trips, Central Florida travelers move internationally on compressed timelines. Here is the destination intelligence that shapes how we serve Orlando clients.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {stats.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">{d.context}</p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">{d.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other Florida Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                We serve travelers across Central and South Florida with the same expedited passport expertise. Looking for service in another community?
              </p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/miami" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Miami <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/tampa" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Tampa <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/jacksonville" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Jacksonville <i className="ri-arrow-right-line text-xs"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}